// react
import { useContext, useMemo, useState } from 'react';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// types
import {
	Frame,
	Ifr,
	Loan,
	Pagination,
	Transaction,
	TransactionAugmented,
} from '../../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// i18next
import { useTranslation } from 'react-i18next';

// context
import {
	ContextProject,
	ContextUserProjectPermissions,
} from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// ui
import { BtnExport } from '../../../ui/BtnExport/BtnExport';
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { BtnLegendTransaction } from '../../../ui/BtnLegendTransaction/BtnLegendTransaction';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardTransaction } from '../../../cards/CardTransaction/CardTransaction';

// fields
import { SelectTransactionPage } from '../../../fields/SelectTransactionPage/SelectTransactionPage';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// config
import { featureFlags } from '../../../../config/featureFlags';

export interface TableInflowsProps {
	frame?: Frame;
	hasExport?: boolean;
	hasLegend?: boolean;
	hasSearch?: boolean;
	hasSelectPage?: boolean;
	isLoading?: boolean;
	pagination: Pagination;
	refetchTransactions?: () => void;
	transactions: Transaction[];
}

export const TableInflows = ({
	frame = 'PROJECT',
	hasExport = true,
	hasLegend = true,
	hasSearch = true,
	hasSelectPage = true,
	isLoading,
	pagination,
	refetchTransactions,
	transactions,
}: TableInflowsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramLoan = searchParams.get('loan');
	const paramTags = searchParams.get('tags');
	const paramStatus = searchParams.get('status');

	// default
	const defaultFiltersValues = {
		loan: 'all',
		status: 'all',
		tags: [],
	};

	// state
	const [filtersValues, setFiltersValues] = useState({
		loan: paramLoan || defaultFiltersValues.loan,
		status: paramStatus || defaultFiltersValues.status,
		tags: paramTags ? paramTags.split(',') : defaultFiltersValues.tags,
	});
	const [ifrs, setIfrs] = useState<Ifr[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);

	// fetch loans
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/loans?fields=id,code&project=${project?.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setLoans(res.data);
		},
	});

	// fetch ifrs
	useFetch({
		isEnabled: Boolean(project?.id && featureFlags['IFRS']),
		url: `${process.env.REACT_APP_API_URL}/ifrs/reports?project=${project?.id}&status=!DELETED&fields=currency,dateEnd,dateStart,id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setIfrs(res.data);
		},
	});

	// vars
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 2,
			},
			isSortable: true,
			label: t('date'),
			value: 'date',
		},
	];

	headers.push({
		GridProps: {
			lg: 2.5,
		},
		label: t('amount'),
		value: 'amount',
	});

	if (frame === 'PROJECT') {
		headers.push({
			GridProps: {
				lg: 1.5,
			},
			label: t('account'),
			value: 'account',
		});
	}

	headers.push({
		GridProps: {
			lg: 1.5,
		},
		label: t('status'),
		value: 'status',
	});

	headers.push({
		GridProps: {
			lg: 2,
		},
		label: t('updated'),
		value: 'modified',
	});

	const idFormFilters = 'form-inflows-filters';

	const optionsStatus = [
		{ label: t('completed'), value: 'COMPLETED' },
		{ label: t('failed'), value: 'FAILED' },
		{ label: t('reverted'), value: 'REVERTED' },
	];

	const optionsLoans = loans.map((loan) => {
		return {
			label: loan.code,
			value: String(loan.id),
		};
	});

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// filters
	const filters: FilterProps[] = [];

	filters.push({
		hasAllOption: true,
		hasNoneOption: false,
		hasSearchParam: true,
		id: `${idFormFilters}-status`,
		label: t('status'),
		name: 'status',
		onChange: handleChangeFilterValue,
		options: optionsStatus,
		type: 'SELECT',
		value: filtersValues.status,
	});

	if (userProjectPermissions?.loan?.read) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-loan`,
			label: t('financingSource'),
			name: 'loan',
			onChange: handleChangeFilterValue,
			options: optionsLoans,
			type: 'SELECT',
			value: filtersValues.loan,
		});
	}

	if (userProjectPermissions?.tag?.read) {
		filters.push({
			hasSearchParam: true,
			id: `${idFormFilters}-tags`,
			label: t('tags'),
			multiple: true,
			name: 'tags',
			onChange: handleChangeFilterValue,
			type: 'TAGS',
			value: filtersValues.tags,
		});
	}

	const empty = useMemo(() => {
		const emptyContent = t('empty.inflow.content', {
			defaultValue: t('empty.transactions.content'),
		});
		const emptyTitle = t('empty.inflow.title', { defaultValue: t('empty.transactions.title') });

		const emptyProps: EmptyProps = {
			actions: [],
			content: {
				children: emptyContent,
			},
			IconCircleProps: {
				color: 'grey',
				type: 'replenishment',
			},
			title: { children: emptyTitle },
		};

		// redefine empty content if search params exist
		if ([...searchParams].length && !paramDialog) {
			emptyProps.content = {
				children: t('empty.default.content'),
			};
			emptyProps.IconCircleProps = {
				icon: null,
			};
		}

		return emptyProps;
	}, [paramDialog, searchParams, t]);

	const content = useMemo(() => {
		if (isLoading || !transactions.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{transactions.map((transaction, i) => {
					// add ifrs ids to transaction
					const transactionAugmented: TransactionAugmented = {
						...transaction,
						ifrs: ifrs.filter(
							(ifr) => ifr.dateStart <= transaction.date && ifr.dateEnd >= transaction.date
						),
					};

					return (
						<Grid item key={`payment-${i}`} xs={12}>
							<CardTransaction
								frame={frame}
								refetchTransactions={refetchTransactions}
								transaction={transactionAugmented}
							/>
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, frame, ifrs, isLoading, refetchTransactions, transactions]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					{hasSelectPage && (
						<Grid item xs={12} md={3} lg={2}>
							<SelectTransactionPage value="inflows" />
						</Grid>
					)}
					{hasSearch && (
						<Grid item xs={12} md={true}>
							<FormSearch InputProps={{ placeholder: t('searchByIdOrEntity') }} />
						</Grid>
					)}
					{hasLegend && (
						<Grid item xs={hasExport ? 4 : 6} md="auto">
							<BtnLegendTransaction />
						</Grid>
					)}
					{hasExport && (
						<Grid item xs={hasLegend ? 4 : 6} md="auto">
							<BtnExport resource="PAYMENT" />
						</Grid>
					)}
					<Grid item container xs={hasExport && hasLegend ? 4 : 6} md="auto">
						<BtnFilters
							filters={filters}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={transactions.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
